import { useCallback, useState } from 'react'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { type ConversationCatalogItemComponent } from '../../value-objects/ConversationTurnItem'

const DEFAULT_QUANTITY = 1

export interface UseCatalogItemCartResult {
  selectedItem: Nullable<ConversationCatalogItemComponent>
  quantity: number
  estimatedTotal: number
  selectCatalogItem: (item: ConversationCatalogItemComponent) => void
  collapse: () => void
  changeQuantity: (quantity: number) => void
  addToRequest: () => void
}

export const useCatalogItemCart = (
  sendMessage: (message: string) => boolean
): UseCatalogItemCartResult => {
  const intl = useProcurifyIntl()
  const [selectedItem, setSelectedItem] =
    useState<Nullable<ConversationCatalogItemComponent>>(null)
  const [quantitiesById, setQuantitiesById] = useState<Record<string, number>>(
    {}
  )

  const quantity =
    (selectedItem && quantitiesById[selectedItem.itemId]) ?? DEFAULT_QUANTITY
  const estimatedTotal = selectedItem ? selectedItem.price * quantity : 0

  const selectCatalogItem = useCallback(
    (item: ConversationCatalogItemComponent) => setSelectedItem(item),
    []
  )

  const collapse = useCallback(() => setSelectedItem(null), [])

  const changeQuantity = useCallback(
    (next: number) => {
      if (!selectedItem) return
      setQuantitiesById((prev) => ({ ...prev, [selectedItem.itemId]: next }))
    },
    [selectedItem]
  )

  const addToRequest = useCallback(() => {
    if (!selectedItem) return
    const didSend = sendMessage(
      intl.formatMessage(messages.addToRequest, {
        title: selectedItem.title,
        quantity,
      })
    )
    if (didSend) setSelectedItem(null)
  }, [intl, quantity, selectedItem, sendMessage])

  return {
    selectedItem,
    quantity,
    estimatedTotal,
    selectCatalogItem,
    collapse,
    changeQuantity,
    addToRequest,
  }
}
